import { get, head, replace, upperFirst } from 'lodash/fp';
import { HeroBanner } from '@ahmdigital/ui';
import React from 'react';

import { CONTENT_SECTIONS } from './content/sections';
import { INSURANCE_TYPES } from './constants';
import AddOnsSection from '../../components/sections/add-ons-section';
import Award from './components/award';
import CommonQuestions from './components/common-questions';
import Contact from './components/contact-details';
import CoverInclusions from './components/cover-inclusions';
import CrossSellSection from '../../components/sections/cross-sell-section';
import FeaturesAndBenefits from './components/features-and-benefits';
import FormsAndGuides from './components/forms-and-guides';
import Head from '../../../utils/head';
import Inclusions from './components/inclusions';
import InformationSection from '../../components/sections/information-section';
import ItemSection from '../../components/sections/item-section';
import PageSections from './components/page-sections';
import PopularDestinationsSection from './components/popular-destinations-section';
import Promotions from './components/promotions';
import QuickLinks from '../../components/quick-links';
import StripSection from '../../components/sections/strip-section';
import TermsAndConditions from './components/terms-and-conditions';
import TravelHeaderWithWidget from '../travel-insurance/components/header';
import ValuePropositions from './components/value-propositions';
import type { InsuranceSubTypeValue, InsuranceTypeContent, InsuranceTypeValue } from './types';

// @ts-expect-error - Automatic, Please fix when editing this file
const getComponents = (insuranceType, productOptionValue) => ({
  [CONTENT_SECTIONS.ADD_ONS]: AddOnsSection,
  [CONTENT_SECTIONS.AWARD]: Award,
  [CONTENT_SECTIONS.COMMON_QUESTIONS]: CommonQuestions,
  [CONTENT_SECTIONS.COMMON_QUESTIONS_ALTERNATE]: CommonQuestions,
  [CONTENT_SECTIONS.FEATURES_AND_BENEFITS]: FeaturesAndBenefits,
  [CONTENT_SECTIONS.FORMS_AND_GUIDES]: FormsAndGuides,
  [CONTENT_SECTIONS.HEADER]: insuranceType === INSURANCE_TYPES.TRAVEL ? TravelHeaderWithWidget : HeroBanner,
  [CONTENT_SECTIONS.COVER_INCLUSIONS]: CoverInclusions,
  [CONTENT_SECTIONS.INCLUSIONS]: Inclusions,
  [CONTENT_SECTIONS.OFFER_STRIP]: StripSection,
  [CONTENT_SECTIONS.PROMOTIONS]: Promotions,
  [CONTENT_SECTIONS.PRODUCTS]: ItemSection,
  [CONTENT_SECTIONS.QUICK_LINKS]: QuickLinks,
  [CONTENT_SECTIONS.TERMS_AND_CONDITIONS]: TermsAndConditions,
  [CONTENT_SECTIONS.CONTACT_DETAILS]: Contact,
  [CONTENT_SECTIONS.TILES]: InformationSection,
  [CONTENT_SECTIONS.VALUE_PROPOSITIONS]: ValuePropositions,
  [CONTENT_SECTIONS.CROSS_SELL]: CrossSellSection,
  [CONTENT_SECTIONS.POPULAR_DESTINATIONS]:
    insuranceType === INSURANCE_TYPES.TRAVEL && !productOptionValue ? PopularDestinationsSection : null,
});

export type InsurancePageProps = {
  content: InsuranceTypeContent;
  insuranceType: InsuranceTypeValue;
  productOptionValue?: InsuranceSubTypeValue;
};

const InsurancePage = ({ content, insuranceType, productOptionValue }: InsurancePageProps) => {
  const title = get('metadataObject.title', content) || `${upperFirst(insuranceType)} insurance`;
  const description = get('metadataObject.description', content);
  const titleTemplate = '%s - ahm insurance';

  const brandCampaignData = head(content?.brandCampaigns);

  const metaDataImage = brandCampaignData?.imageObject?.image?.asset?.url || undefined;

  return (
    <>
      <Head titleTemplate={titleTemplate}>
        <title>{title}</title>
        <meta name="og:title" content={replace(titleTemplate, '%s', title)} />
        <meta name="twitter:title" content={replace(titleTemplate, '%s', title)} />
        <meta name="description" content={description} />
        <meta name="og:description" content={description} />
        <meta name="twitter:description" content={description} />
        <meta name="og:image" content={metaDataImage} />
        <meta name="twitter:image:src" content={metaDataImage} />
      </Head>
      <PageSections
        components={getComponents(insuranceType, productOptionValue)}
        content={content}
        insuranceType={insuranceType}
      />
    </>
  );
};

export default InsurancePage;
